import React, { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Menu, X, Phone, LayoutDashboard, History, Home, Volume2, Calendar, MessageSquare, Building2, CreditCard, LogIn, LogOut, User, Shield, Play } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import BusinessSwitcher from './BusinessSwitcher';

const Layout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentBusinessId, setCurrentBusinessId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { user, token, isAuthenticated, isAdmin, logout, updateUser } = useAuth();
  
  // Update currentBusinessId when businessId from URL changes or user changes
  React.useEffect(() => {
    if (businessId) {
      setCurrentBusinessId(businessId);
    } else if (user?.business_id) {
      setCurrentBusinessId(user.business_id);
    }
  }, [businessId, user?.business_id]);
  
  // Handle business switch - update local state and user context
  const handleBusinessChange = (newBusinessId) => {
    setCurrentBusinessId(newBusinessId);
    // Update user context with new business_id so other components can react
    if (user) {
      updateUser({ ...user, business_id: newBusinessId });
    }
  };
  
  // Don't show layout on landing page, pricing, signup, login, try-demo, or admin pages
  const noLayoutPages = ['/', '/pricing', '/signup', '/login', '/try-demo', '/admin', '/sales'];
  if (noLayoutPages.includes(location.pathname)) {
    return <Outlet />;
  }

  // Navigation items for logged-in users (no "Home" - Dashboard is the home)
  const authenticatedNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'DASHBOARD' },
    { path: '/agent-settings', icon: Volume2, label: 'MY PRO' },
    { path: '/calls', icon: History, label: 'CALLS' },
    { path: '/calendar', icon: Calendar, label: 'CALENDAR' },
  ];

  // Navigation for non-authenticated users (public pages)
  const publicNavItems = [
    { path: '/', icon: Home, label: 'HOME' },
    { path: '/pricing', icon: CreditCard, label: 'PRICING' },
  ];

  // Use authenticated nav when logged in, public nav otherwise
  let navItems = isAuthenticated ? authenticatedNavItems : publicNavItems;
  
  // Add Admin-only links for admin users
  if (isAdmin) {
    navItems = [...navItems, { path: '/businesses', icon: Building2, label: 'BUSINESSES' }, { path: '/admin', icon: Shield, label: 'ADMIN' }];
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo - goes to dashboard when logged in, landing page otherwise */}
            <NavLink to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center shadow-lg shadow-primary/20">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-foreground">
                Front<span className="text-primary">DeskPro</span>
              </span>
            </NavLink>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-2.5 xl:px-4 py-2 rounded-lg whitespace-nowrap transition-colors duration-200 ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`
                  }
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  <span className="text-xs xl:text-sm font-medium">
                    {item.label}
                  </span>
                </NavLink>
              ))}
            </div>

            {/* Right side - Theme Toggle & Auth */}
            <div className="flex items-center gap-3">
              {/* Business Switcher - only show when logged in */}
              {isAuthenticated && token && (
                <div className="hidden md:block">
                  <BusinessSwitcher 
                    token={token} 
                    currentBusinessId={currentBusinessId}
                    onBusinessChange={handleBusinessChange}
                  />
                </div>
              )}
              
              <ThemeToggle />
              
              {/* Auth buttons */}
              {isAuthenticated ? (
                <div className="hidden md:flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted">
                    <User className="w-4 h-4 text-primary" />
                    <span className="text-sm text-foreground">{user?.name || user?.email}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleLogout}>
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/login')}
                  className="hidden md:flex"
                >
                  <LogIn className="w-4 h-4 mr-2" /> Sign In
                </Button>
              )}
              
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg bg-muted text-foreground"
                data-testid="mobile-menu-btn"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-card border-t border-border">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-muted'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">
                    {item.label}
                  </span>
                </NavLink>
              ))}
              
              {/* Mobile Auth Section */}
              <div className="border-t border-border mt-2 pt-2">
                {isAuthenticated ? (
                  <>
                    {/* Mobile Business Switcher */}
                    <div className="px-4 py-2">
                      <BusinessSwitcher 
                        token={token} 
                        currentBusinessId={currentBusinessId}
                        onBusinessChange={(newId) => {
                          handleBusinessChange(newId);
                          setIsMobileMenuOpen(false);
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-3 px-4 py-3 text-muted-foreground">
                      <User className="w-5 h-5" />
                      <span className="font-medium">{user?.name || user?.email}</span>
                    </div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-red-500 hover:bg-red-500/10 w-full"
                      data-testid="mobile-logout-btn"
                    >
                      <LogOut className="w-5 h-5" />
                      <span className="font-medium">Logout</span>
                    </button>
                  </>
                ) : (
                  <NavLink
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-muted-foreground hover:bg-muted"
                  >
                    <LogIn className="w-5 h-5" />
                    <span className="font-medium">Sign In</span>
                  </NavLink>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-16 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
